import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowsClockwise,
  Book,
  CircleNotch,
  FloppyDisk,
  Lightning,
  Robot,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@alfred/convex/_generated/api";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { cn, relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/brief")({
  component: BriefPage,
});

function BriefPage() {
  const brief = useQuery(api.briefs.get, {});
  const save = useMutation(api.briefs.save);

  const [draft, setDraft] = React.useState<string>("");
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [mode, setMode] = React.useState<"split" | "edit" | "preview">("split");

  React.useEffect(() => {
    if (brief && !dirty) {
      setDraft(brief.content ?? "");
    }
  }, [brief?.content, brief?.updatedAt]);

  async function onSave() {
    setSaving(true);
    try {
      await save({ content: draft, updatedBy: "ui" });
      setDirty(false);
      toast.success("Brief saved", {
        description: "Alfred will use it on the next ticket run.",
      });
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  function onRevert() {
    setDraft(brief?.content ?? "");
    setDirty(false);
  }

  const charCount = draft.length;
  const approxTokens = Math.round(charCount / 4);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-border/70 bg-background/85 px-4 py-4 backdrop-blur lg:px-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Book size={18} weight="duotone" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">
                  Tech Brief
                </h1>
                <p className="text-xs text-muted-foreground">
                  Authoritative Paul's Job context Alfred prepends to every
                  Cursor prompt (main agent + repo picker).
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ModeSwitch mode={mode} setMode={setMode} />
            <Button
              variant="outline"
              size="sm"
              onClick={onRevert}
              disabled={!dirty}
            >
              <ArrowsClockwise size={14} weight="bold" />
              Revert
            </Button>
            <Button onClick={onSave} disabled={!dirty || saving}>
              {saving ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <FloppyDisk size={14} weight="bold" />
              )}
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <Badge variant="muted">
            <Lightning size={11} weight="bold" />
            {charCount.toLocaleString()} chars
          </Badge>
          <Badge variant="muted">~{approxTokens.toLocaleString()} tokens</Badge>
          <Badge variant={brief?.hasContent ? "success" : "warn"}>
            <Robot size={11} weight="bold" />
            {brief?.hasContent ? "Live in prompts" : "Not yet saved"}
          </Badge>
          {brief?.updatedAt ? (
            <span>
              Last saved {relativeTime(brief.updatedAt)}
              {brief.updatedBy ? ` by ${brief.updatedBy}` : ""}
            </span>
          ) : null}
          {dirty ? (
            <span className="text-[color:var(--brand-orange)]">
              unsaved changes
            </span>
          ) : null}
        </div>
      </header>

      <div className="min-h-0 flex-1 px-4 py-4 lg:px-6">
        {brief === undefined ? (
          <Skeleton className="h-full w-full rounded-2xl" />
        ) : (
          <div
            className={cn(
              "grid h-full min-h-0 gap-3",
              mode === "split"
                ? "lg:grid-cols-2"
                : "grid-cols-1",
            )}
          >
            {(mode === "split" || mode === "edit") && (
              <Card className="flex min-h-0 flex-col overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/60 pb-2">
                  <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                    Markdown source
                  </CardTitle>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Edit me
                  </span>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-1 flex-col p-0">
                  <textarea
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      setDirty(e.target.value !== (brief?.content ?? ""));
                    }}
                    spellCheck={false}
                    className="block h-full w-full flex-1 resize-none border-0 bg-card p-4 font-mono text-xs leading-relaxed text-foreground/90 outline-none focus:ring-0"
                    placeholder="Paste your Markdown brief here…"
                  />
                </CardContent>
              </Card>
            )}
            {(mode === "split" || mode === "preview") && (
              <Card className="flex min-h-0 flex-col overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/60 pb-2">
                  <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                    Live preview
                  </CardTitle>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    What Alfred sees
                  </span>
                </CardHeader>
                <CardContent className="min-h-0 flex-1 overflow-y-auto p-4">
                  <article className="markdown prose prose-sm dark:prose-invert max-w-none">
                    {draft.trim() ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {draft}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-muted-foreground">
                        Empty brief. Paste your Markdown on the left, or run
                        the engine once to auto-seed from{" "}
                        <code>brief/default.md</code>.
                      </p>
                    )}
                  </article>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ModeSwitch({
  mode,
  setMode,
}: {
  mode: "split" | "edit" | "preview";
  setMode: (m: "split" | "edit" | "preview") => void;
}) {
  const options: { id: typeof mode; label: string }[] = [
    { id: "split", label: "Split" },
    { id: "edit", label: "Edit" },
    { id: "preview", label: "Preview" },
  ];
  return (
    <div className="inline-flex rounded-xl border border-border bg-card p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => setMode(o.id)}
          className={cn(
            "rounded-lg px-3 py-1 text-xs font-medium transition-colors",
            mode === o.id
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
